import { createSlice, nanoid } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState, AppThunk } from '../../store';

import { filterDomains, selectFilters } from './filterSlice';
import { selectFavouriteGoalIds } from './favouriteSlice';

type Goals = {
  loading: boolean;
  hasErrors: boolean;
  data: Array<Goal>;
};

export type Goal = {
  id: string;
  schemaVersion: string;
  publisherGoalId?: string;
  publisherName?: string;
  enduserLabel: string;
  publisherGoalLabel?: string;
  publisherGoalDescription?: string;
  domain?: string;
  subDomain?: string;
  tags?: Array<string>;
  maxValue?: number;
};

export const initialState: Goals = {
  loading: false,
  hasErrors: false,
  data: [],
};

export const goalSlice = createSlice({
  name: 'goals',
  initialState,
  reducers: {
    fetching: (state) => {
      state.loading = true;
    },
    fetchSuccess: (state, { payload: goals }: PayloadAction<Array<Goal>>) => {
      state.data = goals;
      state.loading = false;
      state.hasErrors = false;
    },
    fetchFailed: (state) => {
      state.loading = false;
      state.hasErrors = true;
    },
  },
});

export const { fetching, fetchSuccess, fetchFailed } = goalSlice.actions;

export const selectGoals = (state: RootState) => state.goals;

export const getGoalById = (state: RootState, goalId: string) => {
  const { data } = selectGoals(state);
  return data.find((goal: Goal) => goal.id === goalId);
};

type ExcludedParam = {
  excluded?: Array<string>;
};
const excludedSelector = (_: RootState, param?: ExcludedParam) =>
  param?.excluded;

export const selectGoalsWithFilters = createSelector(
  [selectFilters, selectGoals, selectFavouriteGoalIds, excludedSelector],
  (filters, goals, favouriteGoalIds, excluded = []) => {
    const { data = [] } = goals;
    return data
      .filter((goal) =>
        excluded.includes('publishers')
          ? true
          : filters.publishers.length
            ? filters.publishers.includes(goal.publisherName)
            : true,
      )
      .filter((goal) =>
        excluded.includes('domains')
          ? true
          : filters.domains.length
            ? filters.domains.includes(goal.domain)
            : true,
      )
      .filter((goal) =>
        excluded.includes('subDomains')
          ? true
          : filters.subDomains.length
            ? filters.subDomains.includes(goal.subDomain)
            : true,
      )
      .filter((goal) =>
        excluded.includes('tags')
          ? true
          : filters.tags.length
            ? filters.tags.some((tag) => goal.tags.includes(tag))
            : true,
      );
  },
);

export const selectFavouritedGoals = createSelector(
  [(state: RootState) => selectGoalsWithFilters(state), selectFavouriteGoalIds],
  (goals, favouriteGoalIds) => {
    return goals.filter((goal) => favouriteGoalIds.includes(goal.id));
  },
);

export const selectPublishers = createSelector(selectGoals, (goals) => {
  const publishers = goals.data.map((goal) => goal.publisherName);
  // return only unique publisher names
  return [...new Set(publishers)];
});

const excludePublishersParam = { excluded: ['publishers'] };
export const selectPublishersWithFilters = createSelector(
  (state: RootState) => selectGoalsWithFilters(state, excludePublishersParam),
  (goals) => {
    const publishers = goals
      .flatMap((goal) => goal.publisherName)
      .filter((publisher) => !!publisher && publisher.length);
    // return only unique domains
    return [...new Set(publishers)];
  },
);

const excludeDomainsParam = { excluded: ['domains'] };
export const selectDomainsWithFilters = createSelector(
  (state: RootState) => selectGoalsWithFilters(state, excludeDomainsParam),
  (goals) => {
    const domains = goals
      .flatMap((goal) => goal.domain)
      .filter((domain) => !!domain && domain.length);
    // return only unique domains
    return [...new Set(domains)];
  },
);

const excludeSubDomainsParam = { excluded: ['subDomains'] };
export const selectSubDomainsWithFilters = createSelector(
  (state: RootState) => selectGoalsWithFilters(state, excludeSubDomainsParam),
  (goals) => {
    const subDomains = goals
      .flatMap((goal) => goal.subDomain)
      .filter((subDomain) => !!subDomain && subDomain.length);
    // return only unique subDomains
    return [...new Set(subDomains)];
  },
);

const excludeTagsParam = { excluded: ['tags'] };
export const selectTagsWithFilters = createSelector(
  (state: RootState) => selectGoalsWithFilters(state, excludeTagsParam),
  (goals) => {
    const tags = goals.flatMap((goal) => goal.tags).filter((tag) => tag !== '');
    // return only unique tags
    return [...new Set(tags)];
  },
);

export default goalSlice.reducer;

// asynchronous thunk action
export function fetchGoalsIfEmpty(): AppThunk {
  return async (dispatch, getState) => {
    const { data } = selectGoals(getState());
    if (data.length) return;

    dispatch(fetching());

    try {
      const json = require('/src/data/goals.json');
      const data = json;

      dispatch(fetchSuccess(data));
    } catch (error) {
      console.warn({ error });
      dispatch(fetchFailed());
    }
  };
}
